import type { HTMLAttributes, ReactElement } from 'react';
import { User as UserIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { getInitials } from '../utils/getInitials';

import styles from './Avatar.module.scss';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
    /** Photo URL; when missing, the initials of `name` are shown, then a person icon. */
    src?: string | null;
    /** Alt text of the photo. @default '' */
    alt?: string;
    /** Name for the initials (first letters of the first two words). */
    name?: string | null;
    /** @default 'md' */
    size?: AvatarSize;
}

/**
 * A round user picture: the photo, or the initials, or a person icon.
 *
 * @example
 * <Avatar src={review.author.avatar} name={review.author.name} size="sm" />
 */
export function Avatar({ src, alt = '', name, size = 'md', className, ...props }: AvatarProps): ReactElement {
    const initials = getInitials(name);

    const renderContent = (): ReactElement => {
        if (src) {
            return <img src={src} alt={alt} className={styles.image} />;
        }

        if (initials) {
            return <span className={styles.initials}>{initials}</span>;
        }

        return <UserIcon className={styles.icon} />;
    };

    return (
        <div className={classNames(styles.avatar, styles[`size-${size}`], className)} {...props}>
            {renderContent()}
        </div>
    );
}
